/**
 * ThemeLayout — a tenant's sizes and grid lines, beside its ThemeColors.
 *
 * Like the palette it is a PARTIAL record: an option nobody set is absent and
 * the grid keeps its own look (alternate rows on, no vertical lines, 12px cells
 * on desktop). Only what was chosen is injected, as one `<style>` keyed on the
 * marker classes the grid and the sheet tabs carry (`nt-grid`, `nt-grid-row`,
 * `nt-grid-cell`, `nt-grid-head`, `nt-grid-group`, `nt-sheet-tab`). Colours for
 * the same parts stay where they were: theme roles.
 */

export interface ThemeLayout {
  /** `false` paints every row like the first one. */
  readonly altRows?: boolean;
  /** `true` draws a line between columns in the body. */
  readonly verticalLines?: boolean;
  /** px */
  readonly rowHeight?: number;
  readonly cellFontSize?: number;
  readonly headerFontSize?: number;
  readonly groupFontSize?: number;
  readonly groupRowHeight?: number;
  readonly sheetNameFontSize?: number;
}

export type ThemeLayoutSize = 'rowHeight' | 'cellFontSize' | 'headerFontSize' | 'groupFontSize' | 'groupRowHeight' | 'sheetNameFontSize';

/** What each size may be, so a typo cannot make a sheet unreadable. */
export const THEME_LAYOUT_LIMITS: Readonly<Record<ThemeLayoutSize, { min: number; max: number; placeholder: number }>> = {
  rowHeight: { min: 20, max: 80, placeholder: 36 },
  cellFontSize: { min: 9, max: 20, placeholder: 12 },
  headerFontSize: { min: 9, max: 20, placeholder: 12 },
  groupFontSize: { min: 9, max: 20, placeholder: 12 },
  groupRowHeight: { min: 20, max: 80, placeholder: 36 },
  sheetNameFontSize: { min: 10, max: 22, placeholder: 14 },
};

const SIZE_KEYS = Object.keys(THEME_LAYOUT_LIMITS) as ThemeLayoutSize[];

/** Keeps the known, in-range entries and discards anything else. */
export function sanitizeThemeLayout(value: unknown): ThemeLayout {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  const v = value as Record<string, unknown>;
  const out: Record<string, boolean | number> = {};
  if (typeof v.altRows === 'boolean') out.altRows = v.altRows;
  if (typeof v.verticalLines === 'boolean') out.verticalLines = v.verticalLines;
  for (const key of SIZE_KEYS) {
    const n = v[key];
    const { min, max } = THEME_LAYOUT_LIMITS[key];
    if (typeof n === 'number' && Number.isFinite(n)) out[key] = Math.min(max, Math.max(min, Math.round(n)));
  }
  return out;
}

const STYLE_ELEMENT_ID = 'numtable-theme-layout';

/** The CSS for a layout; `''` when it sets nothing. Unlayered, so it beats the utility classes. */
export function themeLayoutCss(layout: ThemeLayout): string {
  const rules: string[] = [];
  // Higher than the palette's `:root:root[data-theme]`, so a tenant's deep band colour is replaced too.
  if (layout.altRows === false) rules.push(':root:root:root:root{--cell-edit-deep:var(--cell-edit-pale);--cell-ro-deep:var(--cell-ro-pale);}');
  if (layout.verticalLines) rules.push('table.nt-grid td.nt-grid-cell{border-right:1px solid var(--grid-line);}');
  if (layout.rowHeight) rules.push(`table.nt-grid tr.nt-grid-row{height:${layout.rowHeight}px;}`);
  if (layout.cellFontSize) rules.push(`table.nt-grid td.nt-grid-cell,table.nt-grid td.nt-grid-cell *{font-size:${layout.cellFontSize}px;}`);
  if (layout.headerFontSize) rules.push(`table.nt-grid th.nt-grid-head,table.nt-grid th.nt-grid-head *{font-size:${layout.headerFontSize}px;}`);
  if (layout.groupFontSize) rules.push(`table.nt-grid tr.nt-grid-group td,table.nt-grid tr.nt-grid-group td *{font-size:${layout.groupFontSize}px;}`);
  if (layout.groupRowHeight) rules.push(`table.nt-grid tr.nt-grid-group{height:${layout.groupRowHeight}px;}`);
  if (layout.sheetNameFontSize) rules.push(`.nt-sheet-tab,.nt-sheet-tab *{font-size:${layout.sheetNameFontSize}px;}`);
  return rules.join('');
}

/** Injects the layout, or removes it for `null`/an empty one. */
export function applyThemeLayout(layout: ThemeLayout | null): void {
  const css = layout ? themeLayoutCss(layout) : '';
  const existing = document.getElementById(STYLE_ELEMENT_ID);
  if (!css) { existing?.remove(); return; }
  const element = existing ?? document.createElement('style');
  if (!existing) {
    element.id = STYLE_ELEMENT_ID;
    document.head.appendChild(element);
  }
  element.textContent = css;
}
